import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SERVICE_OPTIONS } from "../data/site";

const envBase = process.env.REACT_APP_BACKEND_URL;
const apiBase = (() => {
  try {
    const envHost = new URL(envBase).hostname.replace(/^www\./, "");
    const pageHost = window.location.hostname.replace(/^www\./, "");
    return envHost === pageHost ? window.location.origin : envBase;
  } catch {
    return envBase;
  }
})();
const API = `${apiBase}/api`;

const empty = { name: "", phone: "", service: "", preferred_date: "", preferred_time: "", message: "", home_visit_location: "" };

export const ContactForm = ({ defaultService = "", source = "contact_form" }) => {
  const [params] = useSearchParams();
  const initialService = defaultService || (SERVICE_OPTIONS.includes(params.get("service")) ? params.get("service") : "");
  const [form, setForm] = useState({ ...empty, service: initialService });

  const paramService = params.get("service");
  useEffect(() => {
    if (paramService && SERVICE_OPTIONS.includes(paramService)) {
      setForm((f) => ({ ...f, service: paramService }));
    }
  }, [paramService]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service) {
      toast.error("Please fill in your name, phone and required service.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, { ...form, source });
      setDone(true);
      toast.success("Thank you. Our clinic will contact you shortly.");
      setForm({ ...empty, service: initialService });
    } catch (err) {
      toast.error("Something went wrong. Please call us at 8763027916.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-[2rem] border border-dash-line bg-white p-10 text-center shadow-[0_20px_40px_rgb(10,37,64,0.06)]" data-testid="contact-success">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-dash-teal/10 text-dash-teal">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h3 className="mt-6 font-display text-2xl font-bold text-dash-ink">Thank you.</h3>
        <p className="mt-3 text-dash-muted">Our clinic will contact you shortly.</p>
        <button onClick={() => setDone(false)} data-testid="contact-reset" className="mt-6 rounded-full bg-dash-ink px-6 py-2.5 text-sm font-semibold text-white hover:bg-dash-teal transition-colors">
          Send another request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="rounded-[2rem] border border-dash-line bg-white p-7 md:p-10 shadow-[0_20px_40px_rgb(10,37,64,0.06)]">
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="sm:col-span-1">
          <Label htmlFor="name" className="text-dash-ink font-medium">Name</Label>
          <Input id="name" data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Your full name" className="mt-2 h-12 rounded-xl border-dash-line focus-visible:ring-dash-teal" />
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="phone" className="text-dash-ink font-medium">Phone Number</Label>
          <Input id="phone" data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="e.g. 98765 43210" inputMode="tel" className="mt-2 h-12 rounded-xl border-dash-line focus-visible:ring-dash-teal" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-dash-ink font-medium">Service Required</Label>
          <Select value={form.service} onValueChange={set("service")}>
            <SelectTrigger data-testid="select-service" className="mt-2 h-12 rounded-xl border-dash-line focus:ring-dash-teal">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICE_OPTIONS.map((s) => (
                <SelectItem key={s} value={s} data-testid={`service-opt-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {form.service === "Home Visit Physiotherapy" && (
          <div className="sm:col-span-2">
            <Label htmlFor="home-visit-location" className="text-dash-ink font-medium">Home Visit Location</Label>
            <Input id="home-visit-location" data-testid="input-home-visit-location" value={form.home_visit_location} onChange={set("home_visit_location")} placeholder="Your area / locality (e.g. Belpahar, Jharsuguda)" className="mt-2 h-12 rounded-xl border-dash-line focus-visible:ring-dash-teal" />
            <p className="mt-1.5 text-xs text-dash-muted">Home visits are primarily available in Belpahar and the Jharsuguda area.</p>
          </div>
        )}
        <div className="sm:col-span-1">
          <Label htmlFor="date" className="text-dash-ink font-medium">Preferred Date</Label>
          <Input id="date" type="date" data-testid="input-date" value={form.preferred_date} onChange={set("preferred_date")} className="mt-2 h-12 rounded-xl border-dash-line focus-visible:ring-dash-teal" />
        </div>
        <div className="sm:col-span-1">
          <Label htmlFor="time" className="text-dash-ink font-medium">Preferred Time</Label>
          <Input id="time" type="time" data-testid="input-time" value={form.preferred_time} onChange={set("preferred_time")} className="mt-2 h-12 rounded-xl border-dash-line focus-visible:ring-dash-teal" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="message" className="text-dash-ink font-medium">Message</Label>
          <Textarea id="message" data-testid="input-message" value={form.message} onChange={set("message")} placeholder="Tell us briefly how we can help…" rows={4} className="mt-2 rounded-xl border-dash-line focus-visible:ring-dash-teal resize-none" />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="contact-submit"
        className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-dash-teal px-8 py-4 font-semibold text-white shadow-lg shadow-dash-teal/25 transition-all duration-300 hover:bg-dash-ink disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        {loading ? "Sending…" : "Request Appointment"}
      </button>
      <p className="mt-4 text-center text-xs text-dash-muted">We respect your privacy. Your details are used only to contact you about your enquiry.</p>
    </form>
  );
};
